import React from "react";

import { type VariantProps } from "class-variance-authority";

import { type Badge } from "@/components/flowbite/badge/badge";
import { Table } from "@/components/flowbite/table/table";
import { TableBody } from "@/components/flowbite/table/table-body";

import { BasicInformationTableBadgeRow } from "./basic-information-table-badge-row";
import {
  BasicInformationTableBadgesRow,
  type StatusBadgeItem,
} from "./basic-information-table-badges-row";
import { BasicInformationTableLinkRow } from "./basic-information-table-link-row";
import { BasicInformationTableTextRow } from "./basic-information-table-text-row";

type InfoItem =
  | { type: "text"; head: string; value: string }
  | {
      type: "badge";
      head: string;
      value: string;
      variant?: VariantProps<typeof Badge>["variant"];
      valueClassName?: string;
    }
  | { type: "badges"; head: string; badges: StatusBadgeItem[] }
  | { type: "link"; head: string; value: string; href: string };

interface SectionBasicInfoListProps {
  items: InfoItem[];
}

function getBasicInfoRowKey(item: InfoItem): string {
  switch (item.type) {
    case "text":
      return `text-${item.head}`;
    case "badge":
      return `badge-${item.head}-${item.value}`;
    case "badges":
      return `badges-${item.head}-${item.badges.map((badge) => badge.value).join("|")}`;
    case "link":
      return `link-${item.head}-${item.href}`;
  }
}

export const SectionBasicInfoList: React.FC<SectionBasicInfoListProps> = ({
  items,
}) => {
  return (
    <Table
      className="mt-2"
      layout="fixed"
    >
      <TableBody>
        {items.map((item) => {
          if (item.type === "text") {
            return (
              <BasicInformationTableTextRow
                key={getBasicInfoRowKey(item)}
                head={item.head}
                value={item.value}
              />
            );
          }

          if (item.type === "badge") {
            return (
              <BasicInformationTableBadgeRow
                key={getBasicInfoRowKey(item)}
                head={item.head}
                value={item.value}
                valueClassName={item.valueClassName}
                variant={item.variant ?? "default"}
              />
            );
          }

          if (item.type === "badges") {
            return (
              <BasicInformationTableBadgesRow
                key={getBasicInfoRowKey(item)}
                badges={item.badges}
                head={item.head}
              />
            );
          }

          if (item.type === "link") {
            return (
              <BasicInformationTableLinkRow
                key={getBasicInfoRowKey(item)}
                head={item.head}
                href={item.href}
                value={item.value}
              />
            );
          }

          return null;
        })}
      </TableBody>
    </Table>
  );
};